"use client";

/**
 * feature_grid renderer — a richer card grid for "features" / "what you get".
 * Each card shows icon, title, description, and an optional CTA link.
 *
 * Distinct from card_grid: feature cards are wider, with stronger icon
 * treatment and an emphasized inline link rather than a button. Used for
 * "Why share with us" / "Why choose us" sections.
 *
 * Data: section.content { eyebrow, title, subtitle, columns? } +
 *       section.cards [{ icon, title, description, button_text, button_link }]
 */

import { ArrowRight } from "lucide-react";
import type { VisualCmsSection } from "@/types/visual-cms";
import { SECTION_CONTAINER, sectionShellStyle } from "./section-shell";
import { SafeIcon } from "./SafeIcon";
import { CardDescriptionHtml } from "@/components/visual-cms/CardDescriptionHtml";

function readString(c: unknown, key: string): string {
  if (c && typeof c === "object" && !Array.isArray(c)) {
    const v = (c as Record<string, unknown>)[key];
    return typeof v === "string" ? v : "";
  }
  return "";
}

function readColumns(c: unknown): 2 | 3 | 4 {
  if (c && typeof c === "object" && !Array.isArray(c)) {
    const v = (c as Record<string, unknown>).columns;
    if (typeof v === "number" && [2, 3, 4].includes(v)) return v as 2 | 3 | 4;
  }
  return 3;
}

const gridByCols: Record<2 | 3 | 4, string> = {
  2: "sm:grid-cols-2",
  3: "sm:grid-cols-2 lg:grid-cols-3",
  4: "sm:grid-cols-2 lg:grid-cols-4",
};

export function FeatureGridSection({ section }: { section: VisualCmsSection }) {
  const shell = sectionShellStyle(section);
  const content = (section.content as Record<string, unknown>) ?? {};
  const eyebrow = readString(content, "eyebrow");
  const title = readString(content, "title");
  const subtitle = readString(content, "subtitle");
  const cols = readColumns(content);
  const items = (section.cards ?? []).filter((c) => c.is_active !== false);

  return (
    <section className={shell.className} style={shell.style}>
      <div className={SECTION_CONTAINER}>
        {(eyebrow || title || subtitle) && (
          <div className="text-center max-w-2xl mx-auto mb-10">
            {/* {eyebrow && (
              <p className="font-mono text-[10px] font-medium uppercase tracking-[0.14em] text-green-500 mb-2">
                {eyebrow}
              </p>
            )} */}
            {title && (
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-gray-900">
                {title}
              </h2>
            )}
            {subtitle && <p className="mt-2 text-base text-gray-500">{subtitle}</p>}
          </div>
        )}

        <div className={`grid grid-cols-1 ${gridByCols[cols]} gap-5 lg:gap-6`}>
          {items.map((it) => (
            <article
              key={it.id}
              className="group rounded-xl border border-gray-200 bg-white p-6 lg:p-8 transition-all duration-200 hover:border-green-300 hover:shadow-[0_18px_40px_-24px_rgba(30,140,58,0.25)]"
            >
              <div className="relative inline-flex mb-5">
                <div className="absolute inset-0 rounded-full bg-green-200 blur-md opacity-55 group-hover:opacity-85 transition-opacity" />
                <div className="relative w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center text-white shadow-[0_8px_18px_-6px_rgba(30,140,58,0.5)]">
                  <SafeIcon name={it.icon} className="h-5 w-5" />
                </div>
              </div>
              {it.title && (
                <h3 className="font-semibold text-lg text-gray-900 mb-2 leading-snug">
                  {it.title}
                </h3>
              )}
              {it.description && (
                <CardDescriptionHtml
                  as="p"
                  text={it.description}
                  className="text-sm text-gray-500 leading-relaxed"
                  preserveLineBreaks
                />
              )}
              {it.button_text && (
                <a
                  href={it.button_link || "#"}
                  className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-green-700 hover:underline"
                >
                  {it.button_text}
                  <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                </a>
              )}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
